import { useEffect, useState } from "react";
import { IComboBoxOption, IDropdownOption } from "@fluentui/react";
import { TgkTextSource } from "../../interfaces/CoObject";
import { ObjectPanelTabs } from "../co-panel/ObjectPanelComponent";
import { descriptionLanguage } from "../../../utils/helpers/LanguageHelper";
import { fetchAnalyticalWorkspaces } from "../../../api/object/AnalyticalWorkspace";
import { fetchDatasets } from "../../../api/object/Dataset";
import { DatasetField } from "../../../api/models/dataset-field";
import { extractRichTextsFromDatasetFields } from "../../../api/object/DatasetField";
import { CoObjectSourceTextProps } from "./CoObjectSourceTextPanel";

export interface DatasetDropdownOption extends IDropdownOption {
  partitionType: string;
}

const useCoObjectSourceTextPanel = (props: CoObjectSourceTextProps) => {
  const { objectSource, datasetFields, updateCoObjectSourceText, isComponentInstance, isLocked } = props;
  const [analyticalWorkspaceOptions, setAnalyticalWorkspaceOptions] = useState<IDropdownOption[]>([]);
  const [datasetOptions, setDatasetOptions] = useState<DatasetDropdownOption[]>([]);
  const [richTextOptions, setRichTextOptions] = useState<IDropdownOption[]>([]);

  useEffect(() => {
    const fetchAnalyticalWorkspaces = async () => {
      if (objectSource?.dbApplication) {
        try {
          const analyticalWorkspaceOptions = await getAnalyticalWorkspaceOptions(
            objectSource.dbApplication.id.toString()
          );
          setAnalyticalWorkspaceOptions(analyticalWorkspaceOptions);
        } catch (e) {
          // eslint-disable-next-line no-undef
          console.error(e);
        }
      } else {
        setAnalyticalWorkspaceOptions([]);
      }
    };
    fetchAnalyticalWorkspaces();
  }, [objectSource?.dbApplication]);

  useEffect(() => {
    const fetchDatasets = async () => {
      if (objectSource?.analyticalWorkspace) {
        try {
          const datasetOptions = await getDatasetOptions(
            objectSource.dbApplication.id.toString(),
            objectSource.analyticalWorkspace.code.toString()
          );
          setDatasetOptions(datasetOptions);
        } catch (e) {
          // eslint-disable-next-line no-undef
          console.error(e);
        }
      } else {
        setDatasetOptions([]);
      }
    };
    fetchDatasets();
  }, [objectSource?.analyticalWorkspace, analyticalWorkspaceOptions]);

  useEffect(() => {
    const fetchRichTexts = async () => {
      if (objectSource?.dataset) {
        try {
          const richTexts = datasetFields ? extractRichTextsFromDatasetFields(datasetFields) : [];
          setRichTextOptions(getDatasetFieldOptions(richTexts));
        } catch (error) {
          // eslint-disable-next-line no-undef
          console.error("Error fetching rich texts:", error);
        }
      } else {
        setRichTextOptions([]);
      }
    };
    fetchRichTexts();
  }, [objectSource?.dataset, datasetFields, datasetOptions]);

  async function getAnalyticalWorkspaceOptions(dbId: string): Promise<IDropdownOption[]> {
    const analyticalWorkspaces = await fetchAnalyticalWorkspaces(dbId, descriptionLanguage());
    return analyticalWorkspaces.map((analyticalWorkspace) => ({
      key: analyticalWorkspace.code,
      text: analyticalWorkspace.description,
    }));
  }

  async function getDatasetOptions(dbId: string, awCode: string): Promise<DatasetDropdownOption[]> {
    const datasets = await fetchDatasets(dbId, awCode, descriptionLanguage());
    return datasets.map((dataset) => ({
      key: dataset.code,
      text: dataset.description,
      partitionType: dataset.partitionType,
    }));
  }

  function getDatasetFieldOptions(datasetFields: DatasetField[]): IDropdownOption[] {
    if (!datasetFields || datasetFields.length === 0) {
      return [];
    }

    return datasetFields.map((field) => ({
      key: field.id,
      text: field.description,
    }));
  }

  function selectDbComboBox(option: IComboBoxOption) {
    if (option?.key !== objectSource?.dbApplication?.id) {
      const obj = {
        ...objectSource,
        type: "TGK_TEXT",
        dbApplication: { id: String(option.key), desc: option.text },
        analyticalWorkspace: null,
        dataset: null,
        richText: null,
        dimensions: [],
      } as TgkTextSource;
      updateCoObjectSourceText(obj, validateFields(obj), true);
    }
  }

  function validateFields(obj: TgkTextSource): ObjectPanelTabs {
    const tab = { key: props.tab.key, name: props.tab.name, isValid: false };
    if (obj == null) return tab;
    const { dbApplication, analyticalWorkspace, dataset, richText } = obj;
    if (!dbApplication) return tab;
    if (!analyticalWorkspace) return tab;
    if (!dataset) return tab;
    if (!richText) return tab;
    return { ...tab, isValid: true };
  }

  async function handleOnChangeAnalyticalWorkspace(selectedAnalyticalWorkspace: IDropdownOption | undefined) {
    if (selectedAnalyticalWorkspace?.key !== objectSource?.analyticalWorkspace?.code) {
      const obj: TgkTextSource = {
        ...objectSource,
        type: "TGK_TEXT",
        dbApplication: objectSource.dbApplication,
        analyticalWorkspace: {
          code: selectedAnalyticalWorkspace.key.toString(),
          description: selectedAnalyticalWorkspace.text,
        },
        dataset: null,
        richText: null,
        dimensions: [],
      };
      updateCoObjectSourceText(obj, validateFields(obj), true);
    }
  }

  async function handleOnChangeDataset(selectedDataset: DatasetDropdownOption | undefined) {
    if (selectedDataset?.key !== objectSource?.dataset?.code) {
      const obj: TgkTextSource = {
        ...objectSource,
        type: "TGK_TEXT",
        dbApplication: objectSource.dbApplication,
        analyticalWorkspace: objectSource.analyticalWorkspace,
        dataset: {
          code: selectedDataset.key.toString(),
          description: selectedDataset.text,
          partitionType: selectedDataset.partitionType,
        },
        richText: null,
        dimensions: [],
      };
      updateCoObjectSourceText(obj, validateFields(obj), true);
    }
  }

  async function handleOnChangeRichText(selectedDatasetField: IDropdownOption | undefined) {
    if (selectedDatasetField?.key !== objectSource?.richText?.id) {
      const obj: TgkTextSource = {
        ...objectSource,
        type: "TGK_TEXT",
        dbApplication: objectSource.dbApplication,
        analyticalWorkspace: objectSource.analyticalWorkspace,
        dataset: objectSource.dataset,
        richText: {
          id: selectedDatasetField.key.toString(),
          description: selectedDatasetField.text,
        },
        dimensions: [],
      };
      updateCoObjectSourceText(obj, validateFields(obj), true);
    }
  }

  return {
    objectSource,
    selectDbComboBox,
    analyticalWorkspaceOptions,
    handleOnChangeAnalyticalWorkspace,
    datasetOptions,
    handleOnChangeDataset,
    richTextOptions,
    handleOnChangeRichText,
    isComponentInstance,
    isLocked,
  };
};
export default useCoObjectSourceTextPanel;
